import type { ToastLocale } from "@/locales/fr/toast";

import { ENewPasswordNonEligibleReasonType } from "@/types/backoffice/dto/subscriber/subscriber-new-password-dto.ts";

const toastLocale: ToastLocale = {
  toast: {
    photos: {
      upload: {
        success: "Photos has been uploaded",
        error: "An error occurred while uploading photos",
      },
    },
    signature: {
      reminder: {
        success: "Request for signature resent",
        error: "An error occurred while sending signature request",
      },
      validateEsc: {
        success: "ESC file validated",
        error: "An error occurred while validating ESC file",
      },
      resendBankingInformationToken: {
        success: "IBAN entry request has been sent",
        error: "An error occurred while sending IBAN entry request",
      },
      upgradeToEsc: {
        success: "A new ESC signature request has been generated",
        error: "An error occurred while generating ESC signature request",
      },
      cancel: {
        success: "Signature request canceled",
        error: "An error occured while canceling signature request",
      },
      regenerate: {
        success: "Signature request regenerated",
        error: "An error occured while regenerating signature request",
      },
      customerServiceCertificate: {
        success: "Customer service certificate sent",
        error: "An error occured while sending customer service certificate",
      },
    },
    esc: {
      nonEligiblityCriteria: {
        success: "Eligibility criteria have been updated",
        error: "An error occurred while updating eligibility criteria",
      },
    },
    login: {
      error: "Wrong credentials",
      sessionExpired: "Your session has expired. You will be redirected to the login page.",
      noApplicationRights: "You do not have the required rights to access this application.",
    },
    copy: {
      error: {
        notSupported: "Clipboard is not supported (missing permission)",
      },
      success: "Value has been copied to clipboard",
    },
    comfort: {
      cancel: {
        success: "Personalization has been canceled",
        error: "An error occurred while canceling personalization",
      },
      apply: {
        success: "Personalization has been applied",
        error: "An error occurred while applying personalization",
      },
    },
    site: {
      heatPumpDeclarations: {
        edit: {
          success: "Heat pump has been attached to site",
          error: "An error occurred while attaching heat pump to site",
        },
      },
      closeCall: {
        error: "An error occurred while closing folder, please try again later!",
      },
      attachSubscriber: {
        success: "Subscriber has been attached to site",
        error: "An error occurred while attaching subscriber to site",
      },
      access: {
        error: "An error occurred while fetching site, please try again later!",
      },
    },
    deliveryPoint: {
      manualUpdate: {
        success: "MPAN has been updated",
        error: "Error while updating MPAN",
        // Braced linked key: a bare `@:key:` would swallow the trailing colon into the key name
        reasons: {
          blankPdl: "@:{'toast.deliveryPoint.manualUpdate.error'}: the MPAN is required",
          invalidEnedisPdlSize:
            "@:{'toast.deliveryPoint.manualUpdate.error'}: the site is served by the Enedis DSO, whose MPANs are exactly 14 characters long",
          invalidEnedisControlKey:
            "@:{'toast.deliveryPoint.manualUpdate.error'}: the site is served by the Enedis DSO, and the MPAN control key (last 2 characters) does not match",
          invalidFlowForEnedisDso:
            "@:{'toast.deliveryPoint.manualUpdate.error'}: the meter is operated by Enedis: this MPAN cannot be entered manually for a site outside France",
          invalidState:
            "@:{'toast.deliveryPoint.manualUpdate.error'}: manual entry is not possible for an organization site or a site with several meters",
        },
      },
      searchUpdate: {
        success: "MPAN has been updated",
        error: "Error while updating MPAN",
        searchError: "Error while searching MPANs",
        _20003: "Too many results found",
      },
    },
    subscriber: {
      create: {
        error: "Error while submitting subscriber's creation, please try again later!",
      },
      setPrivileged: {
        success: "Subscribed has been added to PSB",
        error: "An error occurred, please try again later!",
      },
      sendAgreementLetter: {
        success: "Agreement letter sent",
        error: "An error occurred, please try again later!",
      },
      transferSignature: {
        success: "Yousign signature opened",
        error: "An error occurred while requesting the signature",
        cancelSuccess: "Yousign signature cancelled",
        cancelError: "An error occurred while cancelling the signature",
      },
      unsetPrivileged: {
        success: "Subscriber has been removed from PSB",
        error: "An error occurred, please try again later!",
      },
      unsetVip: {
        success: "Subscriber has been removed from VIP",
        error: "An error occurred, please try again later!",
      },
      setVip: {
        success: "Subscriber has been added from VIP",
        error: "An error occurred, please try again later!",
      },
      edit: {
        success: "Subscriber has been updated",
        error: "Error while editing subscriber, please try again later!",
      },
      rotation: {
        success: "Subscriber's rotation has been registered",
        error: "Error while submitting subscriber's rotation, please try again later!",
      },
      resetPassword: {
        success: "Password has been reset",
        nonEligibleOnboardedMyV2016:
          "Subscriber is not eligible to MyVoltalis V2 and was onboarded on MyVoltalis V1 because: ",
        nonEligibleNotOnboardedMyV2016: "Subscriber is not eligible to MyVoltalis V2 because: ",
        nonEligible: {
          [ENewPasswordNonEligibleReasonType.PARTNER_IS_EKWATEUR]: "Partner is ekwateur",
          [ENewPasswordNonEligibleReasonType.NO_APPLIANCE_INSTALLED]: "No appliance is installed",
          [ENewPasswordNonEligibleReasonType.MULTISITE]: "Subscriber has multiple active sites",
          [ENewPasswordNonEligibleReasonType.NO_INSTALLATION_DATE]:
            "Site was not installed yet (no installation date found)",
          [ENewPasswordNonEligibleReasonType.INSTALLATION_DATE_IS_TOO_OLD]:
            "Site was installed too long ago (before 2022/06/01)",
          [ENewPasswordNonEligibleReasonType.TOO_MANY_APPLIANCES]: "There is more than 20 appliances installed on site",
          [ENewPasswordNonEligibleReasonType.TERTIARY_SITE]: "Site is a tertiary site",
          [ENewPasswordNonEligibleReasonType.UNKNOWN]: "Unknown reason",
        },
        error: "Error while resetting password, please try again later!",
      },
    },
    subscription: {
      create: {
        success: "Subscription has been created",
        error: "Error while creating subscription, please try again later!",
      },
      addTag: {
        success: "The tag has been added to the subscription",
        error: "An error occurred while adding the tag, please try again later",
      },
      removeTag: {
        success: "The tag has been removed from the subscription",
        error: "An error occurred while removing the tag, please try again later",
      },
      reOpen: {
        success: "Subscription has been re-opened",
        error: "Error while re-opening subscription, please try again later!",
      },
    },
    operation: {
      edit: {
        success: "Operation has been updated",
        error: "Error while editing operation, please try again later!",
        warn: "No value has been changed, update is not necessary",
      },
    },
    sensor: {
      sendSensor: {
        success: "Sensor request has been registered",
        error: "Error while sending the sensor request, please try again later!",
      },
    },
    gatewayShipment: {
      request: {
        success: "Gateway shipment request has been registered",
        error: "Error while sending the gateway shipment request, please try again later!",
      },
    },
    autoDiag: {
      refreshMyV: {
        success: "MyV programs synced",
        error: "Error while synchronizing myv programs, please try again later!",
      },
    },
    support: {
      success: "Support request sent",
      error: "Error while sending support request, please try again later!",
      abuse: {
        success: "Abuse feedback sent",
        error: "Error while sending abuse feedback, please try again later!",
      },
      satisfactionSurvey: {
        success: "Satisfaction survey sent",
        error: "Error while sending satisfaction survey, please try again later!",
      },
    },
    reintervention: {
      edit: {
        success: "Reintervention comments updated",
        error: "Error while updating reintervention comments, please try again later!",
      },
    },
  },
};

export default toastLocale;
